import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { FlexLayout } from '@Styled/utilities';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import ImportVideoForm from './Components/ImportVideoForm';
import VideoImportsTable from './Components/VideoImportsTable';
import { videoImportsFetchAll } from './Slices/VideoImports.slice';
import { IN_FLIGHT_IMPORT_STATUSES } from './Types/VideoImports.types';
import { useIsStandaloneLecture } from '@ContentManagement/ContentModeContext';

// Slow safety-net cadence: freshness comes from the manual Refresh button
// (and page loads) — each fetch makes the backend opportunistically re-poll
// stale in-flight rows, so a manual refresh returns live provider truth.
// The background poll only exists so an abandoned-open tab eventually
// catches up, without burning API calls every few seconds.
const POLL_INTERVAL_MS = 5 * 60_000;

type Props = {
	courseId: number;
};

/**
 * Files tab: paste a Drive/direct URL, import it into Bunny/VdoCipher, watch
 * encode progress, and turn ready imports into ordinary video units.
 */
const FilesTab: FC<Props> = ({ courseId }) => {
	const dispatch = useAppThunkDispatch();
	const isStandaloneLecture = useIsStandaloneLecture();
	const { list, meta, loaders } = useTypedSelector(
		(state) => state.VideoImports
	);

	// Poll whatever page the user is on without re-arming the interval every
	// time meta changes (a fresh meta object arrives with every fetch).
	const pageRef = useRef(1);
	const perPageRef = useRef(10);
	useEffect(() => {
		if (meta?.current_page) pageRef.current = meta.current_page;
		if (meta?.per_page) perPageRef.current = meta.per_page;
	}, [meta]);

	const refresh = useCallback(
		(silent = false) => {
			dispatch(
				videoImportsFetchAll({
					silent,
					course_id: courseId,
					page: pageRef.current,
					per_page: perPageRef.current,
				})
			);
		},
		[courseId, dispatch]
	);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const hasInFlight = useMemo(
		() => list.some((row) => IN_FLIGHT_IMPORT_STATUSES.includes(row.status)),
		[list]
	);

	// Background catch-up poll — silent (no loading state, list only updates
	// on real changes), only while something is in flight and the browser tab
	// is visible; cleared on unmount.
	useEffect(() => {
		if (!hasInFlight) return;
		const interval = setInterval(() => {
			if (document.visibilityState === 'visible') refresh(true);
		}, POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [hasInFlight, refresh]);

	const onAction = useCallback(() => refresh(), [refresh]);

	return (
		<FlexLayout flexDirection="column" gridGap="2.5rem" width="100%">
			{/* standalone lectures are admin-authored — the instructor portal
			    lists the files without importing new ones */}
			{!isStandaloneLecture && (
				<ImportVideoForm courseId={courseId} onCreated={onAction} />
			)}
			<FlexLayout justifyContent="flex-end">
				<RoundButton
					variant="outlined"
					disabled={loaders.fetchAll === 'pending'}
					startIcon={<EdIcon>refresh</EdIcon>}
					onClick={() => refresh()}
				>
					{loaders.fetchAll === 'pending' ? 'Refreshing…' : 'Refresh'}
				</RoundButton>
			</FlexLayout>
			<VideoImportsTable courseId={courseId} onChanged={onAction} />
		</FlexLayout>
	);
};

export default FilesTab;
